import { useUserRegistryFeatureFlag } from "@/utils/featureFlags";
import React, { createContext, useContext, useEffect, useEffectEvent, useRef, useState } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router";
import { useFirebaseAuth } from "../UserProfileContext/UserProfileContext";

type TriggerRedirect = (defaultRedirect?: string) => Promise<void> | void;

const RedirectContext = createContext<{
    triggerRedirect: TriggerRedirect;
}>({
    triggerRedirect: () => Promise.resolve(),
});

function useForceCompletePrivacyPolicy(triggerRedirect: TriggerRedirect) {
    const { userProfile } = useFirebaseAuth();
    const { isEnabled } = useUserRegistryFeatureFlag();
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        if (!isEnabled) {
            return;
        }
        const allowedPaths = ["/complete_signup", "/terms_of_use", "/privacy_policy"];

        if (!allowedPaths.includes(location.pathname) && userProfile && userProfile.acceptedPrivacyPolicy === false) {
            navigate({ pathname: "/complete_signup", search: "?step=2" });
        }
    }, [userProfile, navigate, location, isEnabled]);
}

function sanitizeRedirectTo(value: string | null): string | null {
    if (!value) return null;

    // Decode once; if it's malformed, ignore it.
    let decoded = value;
    try {
        decoded = decodeURIComponent(value);
    } catch {
        return null;
    }

    // Only allow internal absolute paths
    if (!decoded.startsWith("/")) return null;
    if (decoded.startsWith("//")) return null;

    return decoded;
}

/**
 * Context and hook for handling all automatic redirects. If you need to redirect
 * based on auth/profile state, use this instead of useNavigate directly to ensure
 * the redirect logic is consistent across the app.
 *
 * @param children React children
 * @returns
 */
function RedirectProvider({ children }: { children: React.ReactNode }) {
    const { user, isAuthLoading, isUserProfileLoading, userNeedsToCompleteProfile } = useFirebaseAuth();
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();

    const redirecting = useRef(false); // Lock out on redirects
    const [redirectRequested, setRedirectRequested] = useState(false);

    useEffect(() => {
        // Reset redirecting flag when location changes
        if (location.state?.isRedirect) {
            redirecting.current = false;
        }
    }, [location]);

    const triggerRedirect = useEffectEvent((isLoading: boolean, defaultRedirect?: string) => {
        setRedirectRequested(true); // Mark that a redirect was requested, even if things are still loading

        if (isLoading) return Promise.resolve();

        setRedirectRequested(false);
        redirecting.current = true;

        if (user && !user.isAnonymous) {
            if (userNeedsToCompleteProfile) {
                const urlSearchParams = new URLSearchParams(location.search);
                const redirectToParam = urlSearchParams.get("redirectTo");
                const sanitizedRedirectTo = sanitizeRedirectTo(redirectToParam);

                const newUrlSearchParams = new URLSearchParams();
                if (sanitizedRedirectTo) newUrlSearchParams.set("redirectTo", sanitizedRedirectTo);
                return navigate(
                    { pathname: "/complete_signup", search: `?${newUrlSearchParams.toString()}` },
                    { replace: true, state: { isRedirect: true } }
                );
            }

            const redirectTo = searchParams.get("redirectTo");
            const target = sanitizeRedirectTo(redirectTo) || defaultRedirect || "/account";

            const [pathname, search] = target.split("?");
            return navigate({ pathname, search }, { replace: true, state: { from: location, isRedirect: true } });
        }
    });
    useEffect(() => {
        if (redirectRequested && !redirecting.current) {
            triggerRedirect(isAuthLoading || isUserProfileLoading);
        }
    }, [isAuthLoading, isUserProfileLoading, redirectRequested]);

    return (
        <RedirectContext.Provider value={{ triggerRedirect: () => setRedirectRequested(true) }}>
            {children}
        </RedirectContext.Provider>
    );
}

function useRedirect() {
    const context = useContext(RedirectContext);
    if (context === null) {
        throw new Error("useRedirect must be used within a RedirectProvider");
    }
    return context;
}

export { RedirectProvider, useRedirect };
